<template>
    <div class="mt-6 border-t border-gray-100 pt-4">
        <h3 class="mb-3 text-base font-semibold text-gray-900">
            {{ trans('quality_assurance.configuration.modal.notifications.title') }}
        </h3>

        <multi-select-dropdown
            v-model="form.notification_airport_contact_ids"
            :horizontal="false"
            :is-form-field="true"
            :show-searchbar="true"
            :options="airportContactOptions"
            :label="trans('quality_assurance.configuration.modal.notifications.airport_contacts.label')"
            :placeholder="trans('quality_assurance.configuration.modal.notifications.airport_contacts.placeholder')"
            :invalid-feedback="form.errors.notification_airport_contact_ids"
            name="notification_airport_contact_ids"
        />
        <p class="mb-3 text-sm text-gray-500">
            {{ trans('quality_assurance.configuration.modal.notifications.airport_contacts.help') }}
        </p>

        <hierarchical-multi-select
            v-model="form.vendor_selection"
            :horizontal="false"
            :show-searchbar="true"
            :hierarchical-options="vendorContactTree"
            :display-name-builder="vendorDisplayName"
            :search-keys="['name', 'email']"
            :search-placeholder="trans('quality_assurance.configuration.modal.notifications.vendor_contacts.placeholder')"
            :no-results-text="trans('quality_assurance.configuration.modal.notifications.vendor_contacts.no_results')"
            :no-results-hint-text="trans('quality_assurance.configuration.modal.notifications.vendor_contacts.no_results_hint')"
            :label="trans('quality_assurance.configuration.modal.notifications.vendor_contacts.label')"
            :placeholder="trans('quality_assurance.configuration.modal.notifications.vendor_contacts.placeholder')"
            :invalid-feedback="vendorInvalidFeedback"
            name="notification_vendor_contact_ids"
        />
        <p class="mb-3 text-sm text-gray-500">
            {{ trans('quality_assurance.configuration.modal.notifications.vendor_contacts.help') }}
        </p>

        <div class="row items-end">
            <div class="col-3">
                <jet-select
                    v-model="form.notification_lead_time_weeks"
                    :horizontal="false"
                    :invalid-feedback="form.errors.notification_lead_time_weeks"
                    :label="trans('quality_assurance.configuration.modal.notifications.lead_time.label')"
                    :options="leadTimeOptions"
                    :resettable="false"
                />
            </div>
            <div class="col-9">
                <p class="pb-3 text-gray-700">
                    {{ trans('quality_assurance.configuration.modal.notifications.lead_time.suffix') }}
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import JetSelect from '@/Jetstream/Select.vue';
import MultiSelectDropdown from '@/Components/MultiSelectDropdown.vue';
import HierarchicalMultiSelect from '@/Components/HierarchicalMultiSelect.vue';
import { trans } from '@/mixins/i18n.js';

const props = defineProps({
    form: {
        type: Object,
        required: true,
    },
    availableAirportContacts: {
        type: Object,
        required: false,
        default: () => ({ data: [] }),
    },
    vendorContactTree: {
        type: Array,
        required: true,
    },
});

const airportContactOptions = computed(() =>
    (props.availableAirportContacts?.data ?? []).map((contact) => ({ value: contact.id, label: contact.name })),
);

// Chip labels: a whole maintainer shows its name; a single contact shows the
// contact's name (not the location picker's "parent · child" default).
const vendorDisplayName = (maintainer, contact) => (contact ? contact.name : maintainer.name);

// HierarchicalMultiSelect takes a single error string; surface whichever
// vendor-side validation error (whole maintainer or channel) came back.
const vendorInvalidFeedback = computed(() => {
    const key = Object.keys(props.form.errors).find((errorKey) => errorKey.startsWith('notification_vendor_contact'));

    return key ? props.form.errors[key] : undefined;
});

const leadTimeOptions = computed(() => Array.from({ length: 12 }, (_, index) => ({ value: index + 1, label: String(index + 1) })));
</script>
